import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getGenerationsByProduct } from '../../../API/UtilizationReportingDashboard';
import { constructChartData, downloadDivChart, getTimePeriodLabel, utilizationReportConstants, setChartYAxes, baseBarChartOptions } from './utlizationChart';
import { useErrorBoundary } from "react-error-boundary";
import ReportInfoTooltip from "../ReportInfoTooltip";
import Button from 'react-bootstrap/Button';
import ChartDataLabels from "chartjs-plugin-datalabels";
import ChartSegmentTooltip from './ChartSegmentTooltip';
import { createColorPaletteRegistry } from '../ColorRegistry';

PdfGenerationsByProductChart.propTypes = {
    filterState: PropType.object,
    colorRegistry: PropType.object
}

function PdfGenerationsByProductChart(props) {
    const {
        filterState,
        colorRegistry
    } = props;

    const { showBoundary } = useErrorBoundary();
    const [appState] = useContext(AppContext);
    const chartRef = useRef(null);
    const [data, setData] = useState({});
    const [chartData, setChartData] = useState({});
    const [isLoading, setIsLoading] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
    const tooltipTarget = useRef(null);
    const [maxYValue, setMaxYValue] = useState(null);
    const [yAxesStepIncrementValue, setYAxesStepIncrementValue] = useState(1);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productChartData, setProductChartData] = useState(null);
    const segmentTooltipRef = useRef(null);
    const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProduct?.Enabled, appState)?.Value === 'true') || false;
    const indicationTooltipText = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProductIndications?.HelperText?.IndicationToolTip, appState)?.Value;
    const indicationsNoteText = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProductIndications?.HelperText?.IndicationsNote, appState)?.Value;
    const clickBarHelperText = getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProduct?.HelperText?.ClickBar, appState)?.Value;
    const [chartColorRegistry] = useState(() => createColorPaletteRegistry(
        JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.ColorPalette, appState)?.Value) || [],
        { storageKey: 'utilizationReportingDashboard:generationsByProduct:colorpalette' }
    ));

    ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

    const getData = async () => {
        if (filterState?.selectedProductIds == null) {
            return;
        }
        setSelectedProduct(null);
        setProductChartData(null);
        setIsLoading(true);
        await getGenerationsByProduct({
            "timeFilter": filterState.timePeriodId,
            "productIds": filterState.selectedProductIds,
            "geography": filterState.selectedGeography,
            "geographyType": filterState.selectedGeographyType,
            "messageCategory": filterState.messageCategoryTitle,
            "includePriorPeriod": true
        })
            .then((responseJson) => {
                setData(responseJson);
                const filteredDataSet = filterState.allProducts
                    ? responseJson.currentPeriod.dataSets.filter(item => item.label === utilizationReportConstants.allProducts)
                    : responseJson.currentPeriod.dataSets.filter(item => item.label !== utilizationReportConstants.allProducts);

                setChartData({
                    labels: responseJson.currentPeriod.labels, datasets: constructChartData(filteredDataSet, chartColorRegistry, appState),
                });

                setChartYAxes(filteredDataSet, setYAxesStepIncrementValue, setMaxYValue);
            })
            .catch((err) => {
                showBoundary(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const toggleChartTab = (activeTab, selectedData) => {
        const filteredDataSet = filterState.allProducts
            ? selectedData.dataSets.filter(item => item.label === utilizationReportConstants.allProducts)
            : selectedData.dataSets.filter(item => item.label !== utilizationReportConstants.allProducts);

        setChartData({ labels: selectedData.labels, datasets: constructChartData(filteredDataSet, chartColorRegistry, appState) });
        setActiveButton(activeTab);
        setChartYAxes(filteredDataSet, setYAxesStepIncrementValue, setMaxYValue);
        setSelectedProduct(null);
        setProductChartData(null);
    }

    const drillIntoProduct = (productDataset) => {
        const raw = productDataset?.drillDownData;
        // drillDownData may be a plain array or an integer-keyed object
        const entries = Array.isArray(raw) ? raw : Object.values(raw);
        setProductChartData({ chartData, maxYValue, yAxesStepIncrementValue });
        setSelectedProduct(productDataset);
        // normalize name → label so constructChartData can key colors correctly
        const normalized = entries.map(e => ({ ...e, label: e.label ?? e.name }));
        const indicationsWithTotal = [
            ...normalized,
            { label: "Total", data: productDataset.data, totals: productDataset.totals }
        ];
        const indicationDatasets = constructChartData(indicationsWithTotal, chartColorRegistry, appState);
        setChartData({ labels: chartData.labels, datasets: indicationDatasets });
        setChartYAxes(indicationsWithTotal, setYAxesStepIncrementValue, setMaxYValue);
    };

    const goBack = () => {
        if (productChartData) {
            setChartData(productChartData.chartData);
            setMaxYValue(productChartData.maxYValue);
            setYAxesStepIncrementValue(productChartData.yAxesStepIncrementValue);
        }
        setSelectedProduct(null);
        setProductChartData(null);
    };

    const hasDrillDownEntries = (dataset) => {
        const raw = dataset?.drillDownData;
        if (!raw) { return false; }
        return Array.isArray(raw) ? raw.length > 0 : Object.keys(raw).length > 0;
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: 0
        },
        plugins: {
            legend: baseBarChartOptions.legend,
            datalabels: baseBarChartOptions.datalabels,
            title: {
                ...baseBarChartOptions.title,
                text: [
                    getTimePeriodLabel(filterState.timePeriodId, activeButton, true),
                    !filterState.selectedGeography  ? getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllGeographies?.Label, appState)?.Value || null : filterState.selectedGeographyLabel,
                    filterState.messageCategoryTitle
                ]
            },
            tooltip: { ...baseBarChartOptions.tooltip, filter: (item) => !item.dataset.drillDownData },
        },
        scales: {
            x: baseBarChartOptions.scales.x,
            y: {
                ...baseBarChartOptions.scales.y,
                max: maxYValue,
                ticks: {
                    ...baseBarChartOptions.scales.y.ticks,
                    stepSize: yAxesStepIncrementValue
                }
            }
        },
        resizeDelay:  200,
        onHover: (event, elements, chart) => {
            if (event.native?.target) {
                const hoveredDataset = elements.length > 0 ? chartData.datasets[elements[0].datasetIndex] : null;
                event.native.target.style.cursor = !selectedProduct && hasDrillDownEntries(hoveredDataset) ? 'pointer' : 'default';
            }
            if (elements.length > 0 && event.native) {
                const { datasetIndex, index } = elements[0];
                const dataset = chartData.datasets[datasetIndex];
                if (!hasDrillDownEntries(dataset)) {
                    segmentTooltipRef.current?.hide();
                    return;
                }
                const value = dataset?.data?.[index];
                // Anchor on the bar's own midpoint - the same geometry Chart.js's built-in
                // tooltip positioner uses - instead of the raw cursor position, so the tooltip
                // sits on the bar like the automatic one does rather than following the mouse.
                const canvasRect = chart.canvas.getBoundingClientRect();
                const anchorPoint = elements[0].element.tooltipPosition(true);
                segmentTooltipRef.current?.show(
                    canvasRect.left + anchorPoint.x,
                    canvasRect.top + anchorPoint.y,
                    <div className='chart-segment-tooltip-content'>
                        <strong>{chartData.labels[index]}</strong>
                        <div className='d-flex align-items-center gap-1'>
                            <span className='chart-segment-tooltip-swatch' style={{ backgroundColor: dataset?.backgroundColor }} />
                            <span>{dataset?.label}: {value}</span>
                        </div>
                        {!selectedProduct && dataset?.drillDownData && (
                            <>
                                <hr />
                                {indicationTooltipText}
                            </>
                        )}
                    </div>,
                    `${datasetIndex}-${index}`,
                );
            } else {
                segmentTooltipRef.current?.hide();
            }
        },
        onClick: (event, elements) => {
            if (elements.length > 0 && !selectedProduct) {
                const dataset = chartData.datasets[elements[0].datasetIndex];
                if (!hasDrillDownEntries(dataset)) return;
                segmentTooltipRef.current?.hide();
                drillIntoProduct(dataset);
            }
        },
    };

    useEffect(() => {
        if (chartEnabled && appState?.config) {
            getData();
            setActiveButton(utilizationReportConstants.current);
        }
    }, [filterState.selectedProductIds, filterState.timePeriodId, filterState.selectedGeography, filterState.messageCategoryId]);


    let content;
    if (isLoading === null || isLoading) {
        content = (<div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>);
    }
    else {
        content = (
            <>
                <ChartSegmentTooltip ref={segmentTooltipRef} />
                {selectedProduct && (
                    <div className='drilldown-breadcrumb d-flex align-items-center'>
                        <span className='drilldown-breadcrumb-pill'>
                            {selectedProduct.label}
                            <span className='drilldown-breadcrumb-separator'>&rsaquo;</span>
                            <span className='drilldown-breadcrumb-current'>
                                {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProduct?.HelperText?.DrillDownLevelLabel, appState)?.Value}
                            </span>
                        </span>
                        <Button className='reportTextButton' onClick={goBack}>
                            &larr; {getConfigValue(appState.config?.Common?.Back, appState)?.Value || 'Back'}
                        </Button>
                    </div>
                )}
                <div id="pdfGenerationsByProduct-ChartsArea" className='chart-canvas-wrapper' onMouseLeave={() => segmentTooltipRef.current?.hide()}>
                    <Bar className={`chart ${isCollapsed ? 'collapsed' : ''}`} ref={chartRef} options={chartOptions} data={chartData} />
                </div>
                <div className='reportButtons-container'>
                    <Button
                        className='reportTextButton'
                        onClick={() => downloadDivChart('pdfGenerationsByProduct-ChartsArea', getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProduct?.Title, appState)?.Value || null)}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null} <i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
                    </Button>
                </div>
                <div className='chart-indications-notes'>
                    {!selectedProduct && (
                        <p className='chart-indications-note'>{clickBarHelperText}</p>
                    )}
                    {selectedProduct && (
                        <p className='chart-indications-note'>{indicationsNoteText}</p>
                    )}
                </div>
                <div className='reportTabButton-container'>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.current ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.current, data.currentPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} ({filterState.timePeriodTitle})
                    </Button>
                    <Button
                        className={`tab-button ${activeButton === utilizationReportConstants.prior ? "active" : "inactive"}`}
                        onClick={() => { toggleChartTab(utilizationReportConstants.prior, data.priorPeriod); }}
                    >
                        {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            {chartEnabled &&
                <div className='chart-panel'>
                    <div className='chart-header'>
                        <Button className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`} onClick={() => { setIsCollapsed(!isCollapsed) }}>
                            {getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProduct?.Title, appState)?.Value || null}
                            <ReportInfoTooltip ref={tooltipTarget} text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.PdfGenerationsByProduct?.ToolTip, appState)?.Value} />
                        </Button>
                    </div>
                    <div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>
                        {content}
                    </div>
                </div>
            }
        </>
    );
}

export default PdfGenerationsByProductChart;